import type { NavigateFunction } from 'react-router-dom';
import type { FormDisplayMode } from '@/core/types/documents';
import type { FormAction } from '@/shared/hooks/useCustomForm';

export interface OpenFormOptions {
    docType: string;
    action: FormAction;
    docNo?: string;
    params?: string;
    displayMode?: FormDisplayMode;
    forceModal?: boolean;
}

export interface CloseFormOptions {
    navigate: NavigateFunction;
    docType?: string;
    onClose?: () => void;
    displayMode?: FormDisplayMode;
}

const VALID_ACTIONS = ['add', 'edit', 'view'] as const;
const MODAL_MODES: FormDisplayMode[] = ['modal', 'fullscreen', 'sidebar'];

/**
 * Check if the current device is mobile based on screen width
 * Uses a breakpoint of 768px (typical tablet/mobile breakpoint)
 */
const isMobileDevice = (): boolean => {
    if (typeof window === 'undefined') return false;
    return window.innerWidth < 768;
};

const buildFormPath = (docType: string, action: FormAction, docNo?: string, params?: string): string => {
    const basePath = `/form/${docType}/${action}`;
    let path = docNo ? `${basePath}/${docNo}` : basePath;
    
    if (params) {
        const queryString = params.startsWith('?') ? params : `?${params}`;
        path = `${path}${queryString}`;
    }
    
    return path;
};

const validateFormOptions = (action: string, docNo?: string): boolean => {
    if (!VALID_ACTIONS.includes(action.toLowerCase() as FormAction)) {
        console.warn(`Invalid form action: ${action}. Must be 'add', 'edit', or 'view'.`);
        return false;
    }

    if ((action === 'edit' || action === 'view') && !docNo) {
        console.warn(`Document number is required for ${action} action.`);
        return false;
    }

    return true;
};

const getEffectiveDisplayMode = (displayMode?: FormDisplayMode, forceModal?: boolean): FormDisplayMode => {
    // On mobile devices, always use navigation mode instead of modal
    if (isMobileDevice()) {
        return 'navigation';
    }
    
    return forceModal ? 'modal' : (displayMode ?? 'modal');
};

const isModalMode = (displayMode: FormDisplayMode): boolean => {
    return MODAL_MODES.includes(displayMode);
};

const buildModalQueryParams = (
    docType: string,
    action: FormAction,
    docNo: string | undefined,
    params: string | undefined,
    displayMode: FormDisplayMode,
    currentSearch: string
): URLSearchParams => {
    const queryParams = new URLSearchParams(currentSearch);
    
    queryParams.set('formDocType', docType);
    queryParams.set('formAction', action);
    if (docNo) {
        queryParams.set('formDocNo', docNo);
    } else {
        queryParams.delete('formDocNo');
    }
    
    if (params) {
        const existingParams = new URLSearchParams(params.startsWith('?') ? params.substring(1) : params);
        existingParams.forEach((value, key) => queryParams.set(key, value));
    }
    
    queryParams.set('formModal', 'true');
    queryParams.set('formDisplayMode', displayMode);
    
    return queryParams;
};

export const openForm = (navigate: NavigateFunction, options: OpenFormOptions): void => {
    const { docType, action, docNo, params, displayMode, forceModal } = options;

    if (!validateFormOptions(action, docNo)) {
        return;
    }

    const effectiveDisplayMode = getEffectiveDisplayMode(displayMode, forceModal);
    
    if (isModalMode(effectiveDisplayMode)) {
        const currentPath = globalThis.location.pathname;
        const currentSearch = globalThis.location.search;
        const queryParams = buildModalQueryParams(docType, action, docNo, params, effectiveDisplayMode, currentSearch);
        
        navigate(`${currentPath}?${queryParams.toString()}`, {
            state: {
                programmaticOpen: true,
                displayMode: effectiveDisplayMode,
                formDocType: docType,
                formAction: action,
                formDocNo: docNo,
            },
            replace: true,
        });
    } else {
        const formPath = buildFormPath(docType, action, docNo, params);
        navigate(formPath, {
            state: {
                programmaticOpen: true,
                displayMode: effectiveDisplayMode,
            },
            replace: false,
        });
    }
};

export const closeForm = (options: CloseFormOptions): void => {
    const { navigate, docType, onClose, displayMode } = options;

    if (onClose) {
        onClose();
        return;
    }

    if (displayMode === 'modal') {
        navigate(-1);
        return;
    }

    if (docType) {
        navigate(`/master/${docType}`);
    } else {
        navigate(-1);
    }
};

export const formNavigation = {
    openAdd: (navigate: NavigateFunction, docType: string, params?: string, displayMode?: FormDisplayMode, forceModal?: boolean) => {
        openForm(navigate, { docType, action: 'add', params, displayMode, forceModal });
    },

    openEdit: (navigate: NavigateFunction, docType: string, docNo: string, params?: string, displayMode?: FormDisplayMode, forceModal?: boolean) => {
        openForm(navigate, { docType, action: 'edit', docNo, params, displayMode, forceModal });
    },

    openView: (navigate: NavigateFunction, docType: string, docNo: string, params?: string, displayMode?: FormDisplayMode, forceModal?: boolean) => {
        openForm(navigate, { docType, action: 'view', docNo, params, displayMode, forceModal });
    },

    close: closeForm,
};
